import type { Node } from 'slate'
import type { TunaEditor } from '@tunasong/plugin-lib'

type Listener = () => void
type ScalarCompute = (node: Node) => boolean

/**
 * Per-editor selection scalar store.
 *
 * Lifts selection-derived scalar state into a single subscription per editor:
 *
 * 1. Each `editor.onChange` fires `notify()` exactly once — version bumps,
 *    per-version `WeakMap<Node, boolean>` cache is reset, listeners notified.
 * 2. Consumers (one per block) read via `getOrComputeScalar(node, compute)`.
 *    The compute runs at most once per `(version, node)` — first reader pays,
 *    others hit the cache, downstream `useSyncExternalStore` `Object.is` gates
 *    re-renders to scalar transitions.
 *
 * See `docs/superpowers/specs/2026-05-14-selection-subscription-perf-design.md`
 * §4.2 for the rationale.
 */
export interface SelectionStore {
  getVersion: () => number
  subscribe: (listener: Listener) => () => void
  /** Bump version, clear caches, notify listeners. Invoked by the patched onChange. */
  notify: () => void
  /**
   * Return the cached scalar for this `(version, node)`. On miss, invoke `compute`
   * and cache the result. If `compute` throws, return `false` and do not cache —
   * matches the existing try/catch behavior in `isInsideSelectionRange` (handles
   * transient invalid Range during Slate normalization races, GH #1204).
   */
  getOrComputeScalar: (node: Node, compute: ScalarCompute) => boolean
}

const editorStores = new WeakMap<TunaEditor, SelectionStore>()
/** Marker set on editors that have already been patched to notify the store. */
const patchedEditors = new WeakSet<TunaEditor>()

const makeStore = (): SelectionStore => {
  let version = 0
  let cache: WeakMap<Node, boolean> = new WeakMap()
  const listeners = new Set<Listener>()

  return {
    getVersion: () => version,
    subscribe: (listener: Listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    notify: () => {
      version += 1
      // Replace the cache wholesale on every bump. WeakMap correctness is then
      // trivial: stale node entries from prior versions die with the old map.
      cache = new WeakMap()
      for (const listener of listeners) {
        listener()
      }
    },
    getOrComputeScalar: (node: Node, compute: ScalarCompute) => {
      const cached = cache.get(node)
      if (cached !== undefined) {
        return cached
      }
      let result: boolean
      try {
        result = compute(node)
      } catch {
        // Mirrors the try/catch around `isInsideSelectionRange` — transient
        // invalid Range states during teardown / normalization must not
        // propagate to React's commit phase.
        return false
      }
      cache.set(node, result)
      return result
    },
  }
}

/**
 * Get (or lazily create) the SelectionStore attached to this editor.
 *
 * The store is keyed by editor reference via WeakMap, so two editors on the page
 * have two independent stores. Stale entries die with the editor itself.
 *
 * Note: this does NOT install the onChange patch — call `installSelectionStore`
 * at editor construction time (see `create-editor.ts`).
 */
export const getSelectionStore = (editor: TunaEditor): SelectionStore => {
  let store = editorStores.get(editor)
  if (!store) {
    store = makeStore()
    editorStores.set(editor, store)
  }
  return store
}

/**
 * Install the selection store on this editor: lazily create the store and patch
 * `editor.onChange` exactly once to call `store.notify()` after the prior
 * handler runs. Idempotent — calling twice is a no-op for the second call.
 */
export const installSelectionStore = (editor: TunaEditor): SelectionStore => {
  const store = getSelectionStore(editor)
  if (patchedEditors.has(editor)) {
    return store
  }
  patchedEditors.add(editor)

  const original = editor.onChange
  editor.onChange = (options?: Parameters<typeof original>[0]) => {
    // Preserve all prior listeners — slate-react patches onChange too.
    if (original) {
      original(options)
    }
    store.notify()
  }
  return store
}
